import { PropertyClassification, Appeal, AppealRecord, StipulationRecord, JudgementAmount, StipulationStatus, AppealStatus } from "../../../commontypes/types";

const getAsInteger = (value: string | number | null | undefined): number => (
  !value ? 0 : Number.isInteger(value) ? value as number : Number.parseInt(value as string)
);

export const isDeductionAppeal = (appeal: AppealRecord | null | undefined): boolean => (
  appeal ? (
    appeal.deduction.abatement ||
    appeal.deduction.disabledVeteranExemption ||
    appeal.deduction.seniorCitizenOrDisabledPerson ||
    appeal.deduction.veteranDeduction
  ) : false
);

export const isAppealWithdrawn = (appeal: Appeal | null | undefined, ignorePriorJudgment: boolean): boolean => (
  appeal ? (
    (appeal.APPEAL.reasonForWithdrawal !== null && appeal.APPEAL.reasonForWithdrawal !== undefined) ||
    appeal.APPEAL.withdrawn ||
    appeal.APPEAL.status === AppealStatus.Withdrawn || (!ignorePriorJudgment && appeal.JUDGEMENT?.judgementResult?.key === '7')
  ) : false
);

export const calculateAssessmentTotalWithFraction = (
  propertyClass: PropertyClassification | null | undefined,
  land: string | number | null | undefined,
  improvement: string | number | null | undefined,
  abatement: string | number | null | undefined,
  months?: string | number | null | undefined
): number => {
  const landNum = getAsInteger(land);
  const improvementNum = getAsInteger(improvement);
  const abatementNum = getAsInteger(abatement);
  const monthsNum = getAsInteger(months) || 12;

  if (shouldUseFractionalTaxation(propertyClass)) {
    const fractionTaxed = improvementNum / 10000;
    return (landNum * fractionTaxed - abatementNum) * monthsNum / 12;
  }
  return (landNum + improvementNum - abatementNum) * monthsNum / 12;
};

export const calculateAssessmentTotal = (
  propertyClass: PropertyClassification | null | undefined,
  land: string | number | null | undefined,
  improvement: string | number | null | undefined,
  abatement: string | number | null | undefined,
  months?: string | number | null | undefined
): number =>
  Math.round(calculateAssessmentTotalWithFraction(propertyClass, land, improvement, abatement, months));


export const shouldUseFractionalTaxation = (propertyClass: PropertyClassification | null | undefined): boolean =>
  propertyClass?.key === '6A' || propertyClass?.key === '6B';

export const appealHasApprovedStip = (stipulation: StipulationRecord | null | undefined): boolean =>
  stipulation?.status === StipulationStatus.SOLICITOR_APPROVED ||
  stipulation?.status === StipulationStatus.APPELLANT_APPROVED;

export const getDefaultJudgmentAmount = (appeal: Appeal | null | undefined): JudgementAmount => {
  let amounts: JudgementAmount = {
    landAmount: 0,
    buildingAmount: 0,
    abatementAmount: 0,
    total: 0,
  };

  if (appeal) {
    if (appealHasApprovedStip(appeal.STIPULATION)) {
      amounts = {
        landAmount: appeal.STIPULATION.stipAmount.landAmount,
        buildingAmount: appeal.STIPULATION.stipAmount.buildingAmount,
        abatementAmount: appeal.STIPULATION.stipAmount.abatementAmount,
        total: appeal.STIPULATION.stipAmount.total,
      };
      if (appeal.APPEAL.tenAppeal) {
        const mAmount = Number(appeal.STIPULATION.requestedAdditionMonth);
        amounts.monthAmount = appeal.STIPULATION.requestedAdditionMonth;
        amounts.prTotal = Number(Number(appeal.STIPULATION.stipAmount.total) * mAmount / 12).toFixed(0) as any;
      }
    } else if (appeal.JUDGEMENT) {
      amounts = {
        landAmount: appeal.JUDGEMENT.judgementAmount.landAmount,
        buildingAmount: appeal.JUDGEMENT.judgementAmount.buildingAmount,
        abatementAmount: appeal.JUDGEMENT.judgementAmount.abatementAmount,
        total: appeal.JUDGEMENT.judgementAmount.total,
      };
      if (appeal.APPEAL.tenAppeal) {
        amounts.monthAmount = appeal.JUDGEMENT.judgementAmount.monthAmount;
        amounts.prTotal = appeal.JUDGEMENT.judgementAmount.prTotal;
      }
    } else if (appeal.APPEAL.tenAppeal) {
      if (appeal.APPEAL.improvementRecord.originalRecord) {
        const orTotal = calculateAssessmentTotal(
          appeal.APPEAL.improvementRecord.originalRecord.propertyClass,
          appeal.APPEAL.improvementRecord.originalRecord.additionImprovement,
          appeal.APPEAL.improvementRecord.originalRecord.additionLand,
          appeal.APPEAL.improvementRecord.originalRecord.additionAbatement
        );
        const orPrTotal = calculateAssessmentTotal(
          appeal.APPEAL.improvementRecord.originalRecord.propertyClass,
          appeal.APPEAL.improvementRecord.originalRecord.additionImprovement,
          appeal.APPEAL.improvementRecord.originalRecord.additionLand,
          appeal.APPEAL.improvementRecord.originalRecord.additionAbatement,
          appeal.APPEAL.improvementRecord.originalRecord.totalMonthsAssessed
        );
        amounts = {
          landAmount: appeal.APPEAL.improvementRecord.originalRecord.additionLand,
          buildingAmount: appeal.APPEAL.improvementRecord.originalRecord.additionImprovement,
          abatementAmount: appeal.APPEAL.improvementRecord.originalRecord.additionAbatement,
          monthAmount: Number(appeal.APPEAL.improvementRecord.originalRecord.totalMonthsAssessed),
          prTotal: orPrTotal,
          total: orTotal,
        };
      } else {
        const addTotal = calculateAssessmentTotal(
          appeal.APPEAL.improvementRecord.propertyClass,
          appeal.APPEAL.improvementRecord.requestedAdditionImprovement,
          appeal.APPEAL.improvementRecord.requestedAdditionLand,
          appeal.APPEAL.improvementRecord.requestedAdditionAbatement
        );
        const addPrTotal = calculateAssessmentTotal(
          appeal.APPEAL.improvementRecord.propertyClass,
          appeal.APPEAL.improvementRecord.requestedAdditionImprovement,
          appeal.APPEAL.improvementRecord.requestedAdditionLand,
          appeal.APPEAL.improvementRecord.requestedAdditionAbatement,
          appeal.APPEAL.improvementRecord.requestedAdditionMonth
        );
        amounts = {
          landAmount: appeal.APPEAL.improvementRecord.requestedAdditionLand || 0,
          buildingAmount: appeal.APPEAL.improvementRecord.requestedAdditionImprovement || 0,
          abatementAmount: appeal.APPEAL.improvementRecord.requestedAdditionAbatement || 0,
          monthAmount: appeal.APPEAL.improvementRecord.requestedAdditionMonth || 0,
          prTotal: addPrTotal,
          total: addTotal,
        };
      }
    } else {
      amounts = {
        landAmount: appeal.APPEAL.property.currentAssessment.land,
        buildingAmount: appeal.APPEAL.property.currentAssessment.buildingImprovement,
        abatementAmount: appeal.APPEAL.property.currentAssessment.abatement,
        total: appeal.APPEAL.property.currentAssessment.total,
      };
    }
  }
  return amounts;
};

export const getDefaultJudgmentPropertyClass = (appeal: Appeal | null | undefined): PropertyClassification | undefined => (
  appeal?.JUDGEMENT?.judgementPropertyClass ||
  (appealHasApprovedStip(appeal?.STIPULATION) ? appeal!.STIPULATION?.propertyClass : null) ||
  appeal?.APPEAL.improvementRecord?.propertyClass ||
  appeal?.APPEAL.property.classification ||
  undefined
);